import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

// Game screenshots data structure
const gameplayScreenshots = [
  {
    id: 1,
    title: "Deal the Cards",
    description: "Each player receives 5 cards. The remaining cards form the deck with one card face up to start the pile.",
    image: "/assets/gameplay/deal-cards.png", // We'll create placeholder for now
    tip: "Look for cards that can help you form sequences or sets"
  },
  {
    id: 2,
    title: "Make Your Bid",
    description: "Players bid on how many tricks they think they can win. Be strategic - you must win exactly what you bid!",
    image: "/assets/gameplay/bidding.png",
    tip: "Don't overbid! It's better to make a smaller bid than fail a larger one"
  },
  {
    id: 3,
    title: "Play Your Hand",
    description: "Follow suit when possible. The highest card of the lead suit wins the trick, unless a trump is played.",
    image: "/assets/gameplay/playing-hand.png",
    tip: "Keep track of which cards have been played to make better decisions"
  },
  {
    id: 4,
    title: "Win Tricks Strategically",
    description: "Win exactly the number of tricks you bid. Too few or too many and you'll lose points!",
    image: "/assets/gameplay/winning-tricks.png",
    tip: "Sometimes it's smart to lose a trick to achieve your exact bid"
  },
  {
    id: 5,
    title: "Score Points",
    description: "Make your bid exactly and earn points. Miss your bid and lose points. First to reach the target wins!",
    image: "/assets/gameplay/scoring.png",
    tip: "The scoring system rewards accuracy over ambition"
  }
];

const gameFeatures = [
  {
    icon: "🎯",
    title: "Strategic Bidding",
    description: "Master the art of bidding exactly what you can win"
  },
  {
    icon: "🏆",
    title: "Competitive Rankings",
    description: "Climb the leaderboards and earn achievements"
  },
  {
    icon: "👥",
    title: "Multiplayer Fun",
    description: "Play with friends or join public rooms"
  },
  {
    icon: "📊",
    title: "Detailed Stats",
    description: "Track your progress and improve your game"
  },
  {
    icon: "🎮",
    title: "Real-time Play",
    description: "Smooth gameplay with live updates"
  },
  {
    icon: "🎨",
    title: "Beautiful Interface",
    description: "Designed for both desktop and mobile"
  }
];

const LandingPage: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const navigate = useNavigate();

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % gameplayScreenshots.length);
    }, 4000); // 4 seconds per slide

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const handleSlideChange = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleGetStarted = () => {
    navigate('/register');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-overlay"></div>
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                <span className="brand-name">Kadi Ni Tidi</span>
                <span className="subtitle">The Ultimate Card Game Platform</span>
              </h1>
              <p className="hero-description">
                Experience the thrill of traditional Indian card games in a modern, competitive environment. 
                Master the art of strategic bidding, climb the leaderboards, and become a legend!
              </p>
              <div className="hero-buttons">
                <button className="cta-button primary" onClick={handleGetStarted}>
                  🚀 Start Playing Free
                </button>
                <button className="cta-button secondary" onClick={handleSignIn}>
                  👋 Sign In
                </button>
              </div>
            </div>
            <div className="hero-visual">
              <div className="floating-cards">
                <div className="card-float card-1">🂡</div>
                <div className="card-float card-2">🂮</div>
                <div className="card-float card-3">🃑</div>
                <div className="card-float card-4">🃞</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Play Section with Slideshow */}
      <section className="gameplay-section">
        <div className="container">
          <h2 className="section-title">How to Play Kadi Ni Tidi</h2>
          <p className="section-subtitle">
            Learn the game in 5 simple steps and start winning today!
          </p>
          
          <div className="slideshow-container">
            <div className="slideshow-main">
              <div className="slide-image">
                <div className="placeholder-image">
                  <span className="placeholder-icon">
                    {gameplayScreenshots[currentSlide].title.includes('Deal') ? '🂠' :
                     gameplayScreenshots[currentSlide].title.includes('Bid') ? '💭' :
                     gameplayScreenshots[currentSlide].title.includes('Play') ? '🎯' :
                     gameplayScreenshots[currentSlide].title.includes('Win') ? '🏆' : '📊'}
                  </span>
                  <p className="placeholder-text">Game Screenshot</p>
                </div>
              </div>
              
              <div className="slide-content">
                <div className="step-number">Step {currentSlide + 1}</div>
                <h3 className="slide-title">{gameplayScreenshots[currentSlide].title}</h3>
                <p className="slide-description">{gameplayScreenshots[currentSlide].description}</p>
                <div className="slide-tip">
                  <span className="tip-icon">💡</span>
                  <span className="tip-text">{gameplayScreenshots[currentSlide].tip}</span>
                </div>
              </div>
            </div>
            
            {/* Slide Navigation */}
            <div className="slide-navigation">
              <button 
                className="nav-button prev" 
                onClick={() => handleSlideChange((currentSlide - 1 + gameplayScreenshots.length) % gameplayScreenshots.length)}
              >
                ←
              </button>
              
              <div className="slide-dots">
                {gameplayScreenshots.map((_, index) => (
                  <button
                    key={index}
                    className={`slide-dot ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => handleSlideChange(index)}
                  />
                ))}
              </div>
              
              <button 
                className="nav-button next" 
                onClick={() => handleSlideChange((currentSlide + 1) % gameplayScreenshots.length)}
              >
                →
              </button>
            </div>

            {/* Auto-play indicator */}
            <div className="autoplay-indicator">
              <div className={`autoplay-bar ${isAutoPlaying ? 'playing' : 'paused'}`}></div>
              <button 
                className="autoplay-toggle"
                onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              >
                {isAutoPlaying ? '⏸️' : '▶️'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Why Choose Our Platform?</h2>
          <div className="features-grid">
            {gameFeatures.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <h2 className="section-title">Join the Growing Community</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">1000+</div>
              <div className="stat-label">Active Players</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">50K+</div>
              <div className="stat-label">Games Played</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">35</div>
              <div className="stat-label">Achievements</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Online Gaming</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Become a Champion?</h2>
            <p className="cta-description">
              Join thousands of players competing in the ultimate card game experience. 
              It's free to play and takes less than 30 seconds to get started!
            </p>
            <div className="cta-buttons">
              <button className="cta-button primary large" onClick={handleGetStarted}>
                🎮 Start Playing Now
              </button>
              <button className="cta-button secondary large" onClick={handleSignIn}>
                📱 Sign In to Continue
              </button>
            </div>
            <p className="cta-note">
              No downloads required • Play instantly in your browser • Free forever
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <p className="footer-text">
            © 2025 Kadi Ni Tidi Platform. Made with ❤️ for card game enthusiasts.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;